import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import isElectron from 'is-electron';
import appConfig from 'app-config';

import AppInfo from 'flow/components/AppInfo';
import BrandLogo from 'flow/components/nav/BrandLogo';
import Page from 'flow/components/Setting/Page';
import SettingRow from 'flow/components/Setting/SettingRow';
import SettingSection from 'flow/components/Setting/SettingSection';
import SettingsCompanionOtherDesignated from './SettingsCompanionOtherDesignated';
import SettingsPrivacyPolicyLink from './SettingsPrivacyPolicyLink';
import TrackingConsentModal from 'flow/components/settings/TrackingConsentModal';
import Divider from 'flow/components/ui-components/Divider';
import DropdownMenuComponent from 'flow/components/ui-components/DropdownMenuComponent';
import ExternalLink from 'flow/components/ui-components/ExternalLink';
import GeneralLink from 'flow/components/ui-components/GeneralLink';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Toggle from 'flow/components/ui-components/Toggle';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import useHasGlobalStateViewer from 'flow/hooks/useHasGlobalStateViewer';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import useSettingsNavigation from 'flow/hooks/useSettingsNavigation';
import {
    selectAnalyticsConsent,
    selectApplicationLanguage,
    selectIsSinglePaneMode,
    selectUseAsCompanion,
} from 'flow/selectors/app-selectors';
import {
    selectCustomer,
    selectRetailerKey,
} from 'flow/selectors/customer-selectors';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { getChatShortcuts, saveChatShortcuts } from 'flow/utils/browser-utils';
import { IS_UAE_HOST, isRetailerRainbow } from 'flow/utils/browser-utils-TW';
import { getBrand, getBrandKey } from 'flow/brands/brands';
import {
    claimCompanionRole,
    releaseCompanionRole,
} from 'flow/companion/companion-actions';
import { isCompanionModeEnabled } from 'flow/companion/companion-utils';
import { isEtisalatBrand, isClaroBrand } from 'flow/utils/brand-utils';
import { electronActions } from 'flow/utils/electron-utils';
import { isFlowBrandedCustomer } from 'flow/utils/flow-utils';
import { LocalesData } from 'flow/utils/locales-data';
import { generatePath } from 'flow/utils/router-utils';

const SettingsGeneral = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const { hasFeature } = useFeatureFlags();

    const hasGlobalStateViewerToggleButtonEnabled = useHasGlobalStateViewer();
    const { goToSettings } = useSettingsNavigation();

    const isSinglePaneMode = useAppSelector(selectIsSinglePaneMode);
    const appLanguage = useAppSelector(selectApplicationLanguage);
    const customer = useAppSelector((state) =>
        selectCustomer(state, extensionKey),
    );
    const retailerKey = useAppSelector((state) =>
        selectRetailerKey(state, extensionKey),
    );

    const analyticsConsent = useAppSelector(selectAnalyticsConsent);

    const useAsCompanion = useAppSelector(selectUseAsCompanion);
    const companionModeEnabled = useAppSelector(isCompanionModeEnabled);
    // Desktop-only, and hidden unless the backend switch is on for this user: the companion code
    // ships to everyone, so the setting must stay invisible until the feature is enabled for them.
    const showUseAsCompanion = isElectron() && companionModeEnabled;

    const [shortcutToggle, setShortcutToggle] = useState(getChatShortcuts());
    const [theme, setTheme] = useState(
        localStorage.getItem('theme') || 'light',
    );
    const [globalStateViewerToggle, setGlobalStateViewerToggle] = useState(
        localStorage.getItem('show-global-state-viewer') === 'true',
    );
    const [showConsentModal, setShowConsentModal] = useState(false);

    // Listen for theme changes from Electron menu
    useEffect(() => {
        const handleThemeChange = (event: CustomEvent) => {
            setTheme(event.detail);
        };

        window.addEventListener('theme-changed', handleThemeChange);

        return () => {
            window.removeEventListener('theme-changed', handleThemeChange);
        };
    }, []);

    const changeTheme = (value: 'light' | 'dark' | 'auto') => {
        setTheme(() => {
            const newTheme = value || 'light';

            if (newTheme === 'auto') {
                const prefersDark = window.matchMedia(
                    '(prefers-color-scheme: dark)',
                ).matches;
                document.documentElement.classList.toggle('dark', prefersDark);
            } else {
                document.documentElement.classList.toggle(
                    'dark',
                    newTheme === 'dark',
                );
            }

            localStorage.setItem('theme', newTheme);

            electronActions.notifyThemeChanged();

            return newTheme;
        });
    };

    const consentStatusLabel = useMemo(() => {
        if (analyticsConsent === 'accepted')
            return i18next.t('settings.general.tracking.accept.all');
        if (analyticsConsent === 'rejected')
            return i18next.t('settings.general.tracking.reject.all');
        if (analyticsConsent === 'partial')
            return i18next.t('settings.general.tracking.custom');
        return '';
    }, [analyticsConsent, i18next]);

    const hideConsentUI =
        isEtisalatBrand(getBrandKey()) || isClaroBrand(getBrandKey());

    const hasPrivacyPolicy = !!getBrand().brandGeneralPrivacyPolicyUrl;

    const hasTvxSupport =
        !isFlowBrandedCustomer(customer) &&
        !IS_UAE_HOST &&
        !isRetailerRainbow(retailerKey);

    const supportLink =
        LocalesData[appLanguage]?.supportLink || 'https://support.telavox.com';

    return (
        <Page
            header="settings.general"
            onClickBack={isSinglePaneMode ? goToSettings : undefined}
        >
            <RouteLink
                to={routes['settings.general.localechange']}
                description={LocalesData[appLanguage]?.description}
            >
                {i18next.t('settings.language')}
            </RouteLink>

            <SettingRow
                cta={
                    <Toggle
                        value={shortcutToggle}
                        onChange={(e) => {
                            saveChatShortcuts(e);
                            setShortcutToggle(e);
                        }}
                        text={i18next.t('settings.general.chat.description')}
                    />
                }
            >
                {i18next.t('settings.general.chat.description')}
            </SettingRow>

            {showUseAsCompanion && (
                <SettingRow
                    information={i18next.t(
                        'settings.general.companion.use.hint',
                    )}
                    cta={
                        <Toggle
                            value={useAsCompanion}
                            onChange={(enabled) => {
                                dispatch(
                                    enabled
                                        ? claimCompanionRole()
                                        : releaseCompanionRole(),
                                );
                            }}
                            text={i18next.t('settings.general.companion.use')}
                        />
                    }
                >
                    {i18next.t('settings.general.companion.use')}
                </SettingRow>
            )}

            {showUseAsCompanion && <SettingsCompanionOtherDesignated />}

            <Divider />

            <SettingSection>
                {i18next.t('settings.general.theme')}
            </SettingSection>

            <DropdownMenuComponent
                title={i18next.t(`settings.general.theme.${theme}`)}
                onItemSelected={(value: 'light' | 'dark' | 'auto') =>
                    changeTheme(value)
                }
                options={['light', 'dark', 'auto'].map((key) => ({
                    key,
                    label: i18next.t(`settings.general.theme.${key}`),
                }))}
            />

            {hasGlobalStateViewerToggleButtonEnabled && (
                <>
                    <Divider />
                    <SettingRow
                        cta={
                            <Toggle
                                value={globalStateViewerToggle}
                                onChange={(enabled) => {
                                    localStorage.setItem(
                                        'show-global-state-viewer',
                                        enabled ? 'true' : 'false',
                                    );
                                    setGlobalStateViewerToggle(enabled);
                                    history.push(
                                        generatePath(
                                            routes['settings.general'],
                                            { extensionKey },
                                        ),
                                    );
                                }}
                                text="Enable Global State Viewer"
                            />
                        }
                    >
                        {'Enable Global State Viewer'}
                    </SettingRow>
                </>
            )}

            <Divider />

            {hasFeature(FeatureFlags.KEYBOARD_SHORTCUTS) && (
                <>
                    <RouteLink
                        to={routes['settings.general.keyboardShortcuts']}
                        className="py-4"
                    >
                        {i18next.t('settings.keyboard.shortcuts')}
                    </RouteLink>

                    <Divider />
                </>
            )}

            {hasTvxSupport ? (
                <ExternalLink href={supportLink} className="py-4">
                    {i18next.t('Settings.General.FAQ')}
                </ExternalLink>
            ) : null}

            <RouteLink
                to={routes['settings.general.reportlogs']}
                className="py-4"
            >
                {i18next.t('settings.general.logs.send')}
            </RouteLink>

            {!hideConsentUI && (
                <GeneralLink
                    onClick={() => setShowConsentModal(true)}
                    description={consentStatusLabel}
                    className="h-13"
                >
                    {i18next.t('settings.general.tracking.preferences')}
                </GeneralLink>
            )}

            <SettingsPrivacyPolicyLink />

            {(!hideConsentUI || hasPrivacyPolicy) && <Divider />}

            <RouteLink
                to={routes['settings.general.release']}
                description={<div>{appConfig.version}</div>}
            >
                <div className="flex items-center gap-x-2">
                    <div>
                        <BrandLogo
                            type="versionIcon"
                            withEnvironmentBranding={true}
                        />
                    </div>
                    <div>{'Release notes'}</div>
                </div>
            </RouteLink>

            <div className="mt-4">
                <AppInfo />
            </div>

            {!hideConsentUI && (
                <TrackingConsentModal
                    open={showConsentModal}
                    onClose={() => setShowConsentModal(false)}
                    onDismiss={() => setShowConsentModal(false)}
                />
            )}
        </Page>
    );
};

export default SettingsGeneral;
